import { useEffect, useRef, useState } from "react";
import api from "../api/axiosInstance";
import ScreeningTracker from "../components/ScreeningTracker";
import { KpiGrid, RangePicker, TrendChart, OutcomeBreakdown, presetRange } from "../components/ScreeningStatsWidgets";
import { getScreeningStats, apiErrorMessage } from "../api/screeningApi";
import { roleLabel } from "../utils/formatters";
import "../css/ClientList.css";
import "../css/ScreeningTracker.css";

// ADMIN-only dashboard for AI screenings across the whole team.
//
//  1. KPI tiles            — totals for the chosen date range
//  2. Activity trend       — scheduled vs completed per day
//  3. Outcomes             — AI recommendation split + score distribution
//  4. Team performance     — one row per recruiter / team lead
//  5. All screenings       — the full, filterable list (with "Scheduled by")
//
// Clicking a person in the team table focuses the WHOLE page on them:
// the KPIs, charts and the list below all filter to that person.

export default function AdminScreeningDashboard() {
  const [range, setRange] = useState(() => presetRange(30));
  const [focusUserId, setFocusUserId] = useState(""); // "" = everyone
  const [users, setUsers] = useState([]);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const listRef = useRef(null);

  // People for the "Scheduled by" filter (recruiters + team leads).
  useEffect(() => {
    api
      .get("/admin/users")
      .then((res) => setUsers(res.data.users || []))
      .catch(() => setUsers([]));
  }, []);

  // Dashboard numbers — reload when the range or the focused person changes.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    getScreeningStats({ ...range, scheduledBy: focusUserId })
      .then((res) => !cancelled && setStats(res.data))
      .catch((err) => !cancelled && setError(apiErrorMessage(err, "Couldn't load the screening dashboard.")))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [range, focusUserId]);

  const focusUser = users.find((u) => u._id === focusUserId);

  const focusOn = (userId) => {
    setFocusUserId(userId);
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="cl-page st-page">
      <header className="cl-header st-dash-header">
        <div>
          <span className="st-eyebrow">Admin</span>
          <h1>AI Screening Dashboard</h1>
          <p>Track every AI screening your team schedules — from link sent to final report.</p>
        </div>
        <RangePicker value={range} onChange={setRange} disabled={loading} />
      </header>

      {focusUserId && (
        <div className="st-focus" role="status">
          Showing <b>{focusUser ? `${focusUser.name} (${roleLabel(focusUser.role)})` : "one person"}</b> only
          <button type="button" className="st-focus-clear" onClick={() => setFocusUserId("")}>
            Show everyone ✕
          </button>
        </div>
      )}

      {error && (
        <div className="cl-error" role="alert">
          {error}
        </div>
      )}

      <KpiGrid kpis={stats?.kpis} verdicts={stats?.verdicts} loading={loading} />

      <div className="st-dash-grid">
        <section className="st-panel">
          <div className="st-panel-head">
            <h2>Activity</h2>
            <span className="st-muted">Per day, {stats?.range?.timezone === "Asia/Kolkata" ? "IST" : stats?.range?.timezone || ""}</span>
          </div>
          {stats ? <TrendChart trend={stats.trend} /> : <div className="st-chart-empty">Loading…</div>}
        </section>

        <section className="st-panel">
          <div className="st-panel-head">
            <h2>Outcomes</h2>
          </div>
          {stats ? (
            <OutcomeBreakdown verdicts={stats.verdicts} scoreBands={stats.scoreBands} />
          ) : (
            <div className="st-chart-empty">Loading…</div>
          )}
        </section>
      </div>

      <section className="cl-card st-team-card">
        <div className="cl-card-head st-card-head">
          <h2>Team performance</h2>
          <span className="st-muted">Click a person to focus the dashboard on them</span>
        </div>
        <TeamTable team={stats?.team || []} loading={loading} focusUserId={focusUserId} onFocus={focusOn} />
      </section>

      <div ref={listRef} className="st-list-anchor">
        <ScreeningTracker
          isAdmin
          users={users}
          scheduledBy={focusUserId}
          onScheduledByChange={setFocusUserId}
          title={focusUser ? `Screenings scheduled by ${focusUser.name}` : "All AI screenings"}
        />
      </div>
    </div>
  );
}

function TeamTable({ team, loading, focusUserId, onFocus }) {
  if (loading && team.length === 0) return <div className="cl-empty">Loading team…</div>;
  if (team.length === 0) {
    return (
      <div className="cl-empty">
        <strong>No recruiters or team leads yet.</strong>
      </div>
    );
  }

  return (
    <div className="cl-table-wrap">
      <table className="cl-table st-team-table">
        <thead>
          <tr>
            <th>Team member</th>
            <th className="st-num">Scheduled</th>
            <th className="st-num">Links sent</th>
            <th className="st-num">Pending</th>
            <th>Completion</th>
            <th className="st-num">Expired</th>
            <th>Ready for client</th>
            <th>Last scheduled</th>
          </tr>
        </thead>
        <tbody>
          {team.map((m) => {
            const active = focusUserId === String(m.userId);
            return (
              <tr
                key={m.userId}
                className={`st-team-row${active ? " is-editing" : ""}${m.total === 0 ? " is-idle" : ""}`}
                onClick={() => onFocus(active ? "" : String(m.userId))}
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onFocus(active ? "" : String(m.userId)))}
                aria-pressed={active}
                title={active ? "Show everyone" : `Show only ${m.name}`}
              >
                <td>
                  <div className="st-stack-text">
                    <span className="cl-name-text st-ellipsis">
                      {m.name}
                      {m.status === "INACTIVE" && <span className="st-inactive">Inactive</span>}
                    </span>
                    <span className="st-sub">{roleLabel(m.role)}</span>
                  </div>
                </td>
                <td className="st-num">
                  <b>{m.total}</b>
                </td>
                <td className="st-num">{m.sent}</td>
                <td className="st-num">{m.pending}</td>
                <td>
                  {m.completionRate == null ? (
                    <span className="cl-dash">—</span>
                  ) : (
                    <div className="st-meter" title={`${m.completed} of ${m.sent} links completed`}>
                      <div className="st-meter-track">
                        <div className="st-meter-fill" style={{ width: `${m.completionRate}%` }} />
                      </div>
                      <span>
                        {m.completionRate}% <span className="st-muted">({m.completed})</span>
                      </span>
                    </div>
                  )}
                </td>
                <td className="st-num">{m.expired || <span className="cl-dash">0</span>}</td>
                <td>
                  {m.readyRate == null ? (
                    <span className="cl-dash">—</span>
                  ) : (
                    <div className="st-meter" title={`${m.advance} of ${m.reported} reports are "Advance"`}>
                      <div className="st-meter-track">
                        <div className="st-meter-fill st-meter-good" style={{ width: `${m.readyRate}%` }} />
                      </div>
                      <span>
                        {m.readyRate}% <span className="st-muted">({m.advance}/{m.reported})</span>
                      </span>
                    </div>
                  )}
                </td>
                <td>
                  {m.lastScheduledAt ? (
                    new Date(m.lastScheduledAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })
                  ) : (
                    <span className="cl-dash">—</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}